import { z } from 'zod'

import type { ZodInfer } from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import { OrganizationTypeSchema } from './organization.models.ts'
import { NameSchema } from './property.models.ts'
import { TagFilterSchema } from './tag.models.ts'
import {
  BillingProfileIdSchema,
  FileIdSchema,
  OrganizationIdSchema,
  TeamIdSchema,
  TeamUrnSchema,
} from './unique-id.models.ts'

export const TeamDomainSelectModelSchema = z.object({
  teamId: TeamIdSchema,
  teamUrn: TeamUrnSchema,
  organizationType: OrganizationTypeSchema,
  organizationId: OrganizationIdSchema,
  name: NameSchema,
  avatarFileId: FileIdSchema.optional(),
  billingProfileId: BillingProfileIdSchema.optional(),
})

export type TeamDomainSelectModel = ZodInfer<typeof TeamDomainSelectModelSchema>

/**
 * TeamDomainInsertModel
 */
export const TeamDomainInsertModelSchema = TeamDomainSelectModelSchema.omit({
  teamUrn: true,
}).partial({
  teamId: true,
})

export type TeamDomainInsertModel = ZodInfer<typeof TeamDomainInsertModelSchema>

/**
 * TeamDomainUpdateModel
 */
export const TeamDomainUpdateModelSchema = TeamDomainSelectModelSchema.pick({
  name: true,
})
  .extend({
    avatarFileId: TeamDomainSelectModelSchema.shape.avatarFileId.nullable(),
    billingProfileId:
      TeamDomainSelectModelSchema.shape.billingProfileId.nullable(),
  })
  .partial()

export type TeamDomainUpdateModel = ZodInfer<typeof TeamDomainUpdateModelSchema>

/**
 * TeamDomainSelectModelFilters
 */
export const TeamDomainSelectModelFiltersSchema = z
  .object({
    teamId: ModelFilterSchema(TeamIdSchema),
    organizationType: ModelFilterSchema(OrganizationTypeSchema),
    organizationId: ModelFilterSchema(OrganizationIdSchema),
    // When filtering, transform names to lower case so the
    // "lower(teams.name)" index can be used.
    name: ModelFilterSchema(NameSchema.toLowerCase()),
    billingProfileId: z.union([
      ModelFilterSchema(BillingProfileIdSchema),
      z.literal('undefined'),
    ]),
    tags: TagFilterSchema,
  })
  .partial()

export type TeamDomainSelectModelFilters = ZodInfer<
  typeof TeamDomainSelectModelFiltersSchema
>
